/**
 * Converts a number of seconds into a string formatted as "MM:SS".
 *
 * @param seconds - The total number of seconds to format.
 * @returns The formatted time string in "MM:SS" format, with leading zeros for single-digit minutes or seconds.
 */
export function formatCountdownTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Normalizes a Unix timestamp to seconds.
 *
 * Converts the input to seconds if it is in milliseconds; otherwise, returns the value unchanged.
 *
 * @param timestamp - A Unix timestamp, in seconds or milliseconds.
 * @returns The timestamp in seconds.
 */
export function normalizeTimestamp(timestamp: number): number {
  // Check if it's in milliseconds (timestamps after ~2001-09-09 in milliseconds)
  const MILLISECOND_THRESHOLD = 1000000000000; // 10^12
  return timestamp > MILLISECOND_THRESHOLD ? Math.floor(timestamp / 1000) : timestamp;
}

/**
 * Calculates the number of seconds remaining until a specified expiration timestamp.
 *
 * @param expiresAt - The expiration time as a Unix timestamp, in seconds or milliseconds.
 * @returns The number of seconds remaining until {@link expiresAt}, or 0 if the time has already passed.
 */
export function calculateTimeRemaining(expiresAt: number): number {
  const expiresAtInSeconds = normalizeTimestamp(expiresAt);
  const now = Math.floor(Date.now() / 1000);
  const difference = expiresAtInSeconds - now;
  return Math.max(0, difference);
}
